import { useState, useCallback } from 'react';
import { Loader2, Eye, CheckCircle2, CreditCard } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

export function SubscriptionBeneficiaryBackfillPanel() {
  const [previewing, setPreviewing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);

  const runPreview = useCallback(async () => {
    setPreviewing(true);
    setPreview(null);
    setResult(null);
    try {
      const { data } = await diagnosticsAPI.subscriptionBeneficiaryBackfillPreview();
      setPreview(data);
      if (data.candidate_count === 0) {
        toast.success('No subscription rows need a beneficiary name');
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setPreviewing(false);
    }
  }, []);

  const runApply = useCallback(async () => {
    setApplying(true);
    setResult(null);
    try {
      const { data } = await diagnosticsAPI.subscriptionBeneficiaryBackfillApply();
      setResult(data);
      toast.success(`Backfill complete: ${data.updated} rows enriched`);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setApplying(false);
    }
  }, []);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <CreditCard className="h-5 w-5" />
          Subscription Beneficiary Backfill
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          Re-runs the detection pipeline and enriches existing subscription rows with the best
          beneficiary/merchant name from the underlying transactions, keeping the raw reference as a
          secondary line. Name, amount and fingerprint are never changed (dismissals stay linked).
        </p>

        <div className="flex items-end gap-3 flex-wrap">
          <Button onClick={runPreview} disabled={previewing || applying} variant="outline">
            {previewing ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Eye className="h-4 w-4 mr-2" />
            )}
            Preview
          </Button>
          <Button
            onClick={runApply}
            disabled={!preview || applying || previewing || preview.candidate_count === 0}
          >
            {applying ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <CheckCircle2 className="h-4 w-4 mr-2" />
            )}
            Apply
          </Button>
        </div>

        {preview && (
          <div className="rounded-lg border border-blue-200 dark:border-blue-800 p-3 bg-blue-50/50 dark:bg-blue-950/20">
            <div className="flex items-center gap-2 mb-3">
              <Eye className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              <span className="text-sm font-medium">Preview</span>
              <Badge variant="secondary" className="text-xs">
                {preview.candidate_count} to enrich
              </Badge>
            </div>

            {preview.funnel && (
              <div className="mb-3 grid grid-cols-2 sm:grid-cols-5 gap-2" data-testid="backfill-funnel">
                {[
                  ['Scanned', preview.funnel.rows_scanned, null],
                  ['Already enriched', preview.funnel.already_enriched, 'already_enriched'],
                  ['Enrichable', preview.funnel.matched_with_beneficiary, 'matched_with_beneficiary'],
                  ['No beneficiary', preview.funnel.matched_no_beneficiary, 'matched_no_beneficiary'],
                  ['Unmatchable', preview.funnel.unmatchable, 'unmatchable'],
                ].map(([label, count, bucket]) => {
                  const ids = bucket && preview.funnel.samples ? preview.funnel.samples[bucket] : null;
                  return (
                    <div
                      key={label}
                      className="rounded border bg-background px-2 py-1"
                      title={ids && ids.length ? `e.g. ${ids.join(', ')}` : ''}
                    >
                      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
                      <div className="text-sm font-mono font-medium">{count ?? 0}</div>
                    </div>
                  );
                })}
              </div>
            )}

            {preview.census && Object.keys(preview.census).length > 0 && (
              <div className="mb-3 space-y-3" data-testid="backfill-census">
                <div className="text-xs font-medium text-muted-foreground">
                  Field census (matched transactions, by source)
                </div>
                {Object.entries(preview.census).map(([source, sourceCensus]) => (
                  <div key={source} className="rounded border bg-background overflow-auto">
                    <div className="px-2 py-1 text-xs font-medium bg-muted/50 border-b flex items-center justify-between">
                      <span>{source}</span>
                      <span className="text-muted-foreground font-mono">
                        {sourceCensus.total_matched_transactions} txns
                      </span>
                    </div>
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b bg-muted/30">
                          <th className="px-2 py-1 text-left">Field</th>
                          <th className="px-2 py-1 text-left">Present</th>
                          <th className="px-2 py-1 text-left">Samples</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(sourceCensus.fields || {}).map(([field, stat]) => (
                          <tr key={field} className="border-b last:border-0">
                            <td className="px-2 py-1 font-mono">{field}</td>
                            <td className="px-2 py-1">
                              {stat.present_count} / {sourceCensus.total_matched_transactions}
                            </td>
                            <td className="px-2 py-1 max-w-[280px] truncate" title={(stat.samples || []).join(' | ')}>
                              {(stat.samples || []).join(' | ') || '--'}
                            </td>
                          </tr>
                        ))}
                        {Object.keys(sourceCensus.fields || {}).length === 0 && (
                          <tr>
                            <td className="px-2 py-1 text-muted-foreground italic" colSpan={3}>
                              No candidate field carries a value on any matched transaction.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                ))}
              </div>
            )}

            {preview.rows && preview.rows.length > 0 && (
              <div className="max-h-64 overflow-auto rounded border bg-background">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="px-2 py-1 text-left">Current name</th>
                      <th className="px-2 py-1 text-left">Beneficiary</th>
                      <th className="px-2 py-1 text-left">Reference</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.rows.map((r) => (
                      <tr key={r.id} className="border-b last:border-0">
                        <td className="px-2 py-1 text-muted-foreground italic">{r.name}</td>
                        <td className="px-2 py-1 font-medium">{r.beneficiary}</td>
                        <td className="px-2 py-1 max-w-[160px] truncate" title={r.raw_reference || ''}>
                          {r.raw_reference || '--'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {result && (
          <div className="rounded-lg border border-green-200 dark:border-green-800 p-3 bg-green-50/50 dark:bg-green-950/20">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
              <span className="text-sm font-medium">Applied</span>
            </div>
            <div className="text-xs">
              <span className="text-muted-foreground">Rows enriched:</span>{' '}
              <span className="font-medium text-green-600">{result.updated}</span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
